import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Coins, Download, Film, Gauge, Loader2, RefreshCw, Search, Shield, Timer, Zap,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import SpendChart from "../components/SpendChart";
import SpendJobDetail from "../components/SpendJobDetail";
import SpendAlerts from "../components/SpendAlerts";
import WeeklyDigest from "../components/WeeklyDigest";
import WhisperOverlapAudit from "../components/WhisperOverlapAudit";
import UnitEconomics from "../components/UnitEconomics";

const RANGES = [
  { k: "today", label: "Today" },
  { k: "7d", label: "7d" },
  { k: "30d", label: "30d" },
  { k: "this_month", label: "This month" },
  { k: "last_month", label: "Last month" },
  { k: "all", label: "All time" },
];

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const inr = (v) =>
  v == null ? "—" : `\u20B9${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US"));
const mins = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}m`);

function Kpi({ label, value, sub, icon: Icon, tid }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid={tid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
        {Icon ? <Icon className="w-3 h-3" /> : null} {label}
      </div>
      <div className="font-heading text-3xl font-black tracking-tighter text-white break-all">{value}</div>
      <div className="text-[11px] font-mono text-zinc-500 mt-2">{sub || "\u00A0"}</div>
    </div>
  );
}

export default function AdminSpendPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [granularity, setGranularity] = useState("daily");
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [jobs, setJobs] = useState({ rows: [], total: 0, page: 1, pages: 1 });
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [fxInput, setFxInput] = useState("");
  const [fxBusy, setFxBusy] = useState(false);

  const qs = useCallback(() => {
    const p = new URLSearchParams({ range });
    if (range === "custom") {
      p.set("date_from", custom.from);
      p.set("date_to", custom.to);
    }
    return p.toString();
  }, [range, custom]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const base = qs();
      const [s, se, j] = await Promise.all([
        api.get(`/admin/spend/summary?${base}`),
        api.get(`/admin/spend/series?${base}&granularity=${granularity}`),
        api.get(`/admin/spend/jobs?${base}&q=${encodeURIComponent(q)}&page=${page}&page_size=25`),
      ]);
      setSummary(s.data);
      setSeries(se.data.series || []);
      setJobs(j.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admins only");
        navigate("/dashboard");
      } else {
        toast.error(err?.response?.data?.detail || "Could not load spend data");
      }
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }, [qs, granularity, q, page, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const saveFx = async () => {
    const rate = Number(fxInput);
    if (!rate) return toast.error("Enter a USD→INR rate first");
    setFxBusy(true);
    try {
      await api.post("/admin/spend/fx", { rate });
      toast.success(`USD→INR set to ${rate}`);
      setFxInput("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save rate");
    } finally {
      setFxBusy(false);
    }
  };

  const fetchFx = async () => {
    setFxBusy(true);
    try {
      const { data } = await api.post("/admin/spend/fx/fetch");
      toast.success(`Live rate applied: ${data.rate}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not fetch a live rate");
    } finally {
      setFxBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get(`/admin/spend/export.csv?${qs()}&q=${encodeURIComponent(q)}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `linguasub_spend_${range}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  if (loading) {
    return (
      <div className="grain-bg min-h-screen bg-zinc-950 text-white grid place-items-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  const k = summary?.kpis || {};
  const ce = summary?.cascade_economics || {};
  const counters = summary?.counters || {};
  const fx = summary?.fx || {};

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white" data-testid="admin-spend-page">
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Shield className="w-4 h-4 text-emerald-400 shrink-0" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">Admin</div>
            <div className="font-heading text-lg font-bold tracking-tight truncate">Spend Info</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCsv}
              data-testid="spend-export-csv-btn"
              className="border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest inline-flex items-center gap-1.5"
            >
              <Download className="w-3 h-3" /> CSV
            </button>
            <button
              type="button"
              onClick={load}
              disabled={busy}
              data-testid="spend-refresh-btn"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
            <Link
              to="/admin"
              data-testid="spend-back-to-admin"
              className="border border-zinc-800 hover:bg-zinc-800 px-3 py-1.5 text-[10px] uppercase font-mono tracking-widest inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3 h-3" /> Admin
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        {/* Scope disclosure */}
        <section
          className="border border-amber-500/25 bg-amber-500/[0.04] p-4 space-y-1.5"
          data-testid="spend-scope-note"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">
            Actual AI spend only · infrastructure excluded
          </div>
          {(summary?.notes || []).map((n) => (
            <p key={n.slice(0, 28)} className="text-xs text-zinc-400 leading-relaxed">
              {n}
            </p>
          ))}
          <p className="text-[11px] font-mono text-zinc-500 pt-1">
            Pricing version {summary?.pricing_version} · telemetry{" "}
            {summary?.telemetry_installed ? "active" : "NOT installed"} · observational only, customer
            billing untouched
          </p>
        </section>

        {/* Controls */}
        <section className="flex flex-wrap items-center gap-3">
          <div className="inline-flex gap-px bg-zinc-800 border border-zinc-800" data-testid="spend-range-picker">
            {RANGES.map((r) => (
              <button
                key={r.k}
                type="button"
                onClick={() => {
                  setPage(1);
                  setRange(r.k);
                }}
                disabled={busy}
                data-testid={`spend-range-${r.k}`}
                className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest transition-colors ${
                  range === r.k ? "bg-white text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="inline-flex items-center gap-2">
            <input
              type="date"
              value={custom.from}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
              data-testid="spend-custom-from"
              className="h-8 px-2 bg-zinc-950 border border-zinc-800 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span className="text-zinc-600 text-xs">→</span>
            <input
              type="date"
              value={custom.to}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
              data-testid="spend-custom-to"
              className="h-8 px-2 bg-zinc-950 border border-zinc-800 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => {
                if (!custom.from || !custom.to) return toast.error("Pick both dates");
                setPage(1);
                setRange("custom");
              }}
              data-testid="spend-custom-apply"
              className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest border ${
                range === "custom"
                  ? "bg-white text-zinc-950 border-white"
                  : "border-zinc-800 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              Custom
            </button>
          </div>

          <div className="inline-flex gap-px bg-zinc-800 border border-zinc-800 ml-auto" data-testid="spend-granularity">
            {["daily", "weekly", "monthly"].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGranularity(g)}
                data-testid={`spend-granularity-${g}`}
                className={`h-8 px-3 text-[10px] font-mono uppercase tracking-widest ${
                  granularity === g ? "bg-white text-zinc-950" : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </section>

        {/* FX control */}
        <section className="border border-zinc-800 bg-zinc-900/40 p-4 space-y-3" data-testid="spend-fx-panel">
          <div className="flex flex-wrap items-center gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">USD → INR rate</div>
            <div className="text-lg font-mono text-white mt-0.5" data-testid="spend-fx-rate">
              {fx.configured ? fx.rate : "Not set — INR hidden"}
            </div>
            <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
              {fx.configured
                ? `${fx.source === "manual" ? "manual" : "fetched"} · set ${
                    fx.set_at ? new Date(fx.set_at).toLocaleString() : "—"
                  }${fx.set_by ? ` by ${fx.set_by}` : ""}`
                : "Events already recorded keep whatever rate was live when they were written"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={fxInput}
              onChange={(e) => setFxInput(e.target.value)}
              placeholder="e.g. 88.25"
              inputMode="decimal"
              data-testid="spend-fx-input"
              className="h-9 px-3 w-32 bg-zinc-950 border border-zinc-800 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={saveFx}
              disabled={fxBusy}
              data-testid="spend-fx-save-btn"
              className="h-9 px-3 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-300 disabled:opacity-40"
            >
              Save rate
            </button>
            <button
              type="button"
              onClick={fetchFx}
              disabled={fxBusy}
              data-testid="spend-fx-fetch-btn"
              className="h-9 px-3 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:border-blue-500/50 hover:text-blue-300 disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              {fxBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Fetch live
            </button>
          </div>
          </div>
          {counters.events_without_inr > 0 && (
            <p className="text-[11px] font-mono text-amber-300/80" data-testid="spend-inr-missing-note">
              {int(counters.events_without_inr)} of {int(counters.events)} cost events were recorded while no
              USD→INR rate was configured. Their INR value stays empty and is excluded from INR totals — USD is
              the authoritative figure for those events. Nothing is back-converted or estimated.
            </p>
          )}
        </section>

        {/* KPIs */}
        <section className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="spend-kpis">
          <Kpi
            label="Total AI spend"
            value={usd(k.total_spend_usd)}
            sub={k.total_spend_inr != null ? inr(k.total_spend_inr) : "INR needs an exchange rate"}
            icon={Coins}
            tid="spend-kpi-total"
          />
          <Kpi
            label="Videos with telemetry"
            value={int(k.total_videos)}
            sub={`${int(counters.jobs_without_telemetry)} job(s) in range without telemetry`}
            icon={Film}
            tid="spend-kpi-videos"
          />
          <Kpi
            label="Video minutes"
            value={mins(k.total_video_minutes)}
            sub="actual media duration processed"
            icon={Timer}
            tid="spend-kpi-minutes"
          />
          <Kpi
            label="Avg cost / video"
            value={usd(k.avg_cost_per_video_usd)}
            sub={k.avg_cost_per_video_inr != null ? inr(k.avg_cost_per_video_inr) : "—"}
            icon={Gauge}
            tid="spend-kpi-avg-video"
          />
          <Kpi
            label="Avg cost / minute"
            value={usd(k.avg_cost_per_minute_usd)}
            sub={k.avg_cost_per_minute_inr != null ? inr(k.avg_cost_per_minute_inr) : "—"}
            icon={Gauge}
            tid="spend-kpi-avg-minute"
          />
          <Kpi
            label="GPT-5.4 escalation spend"
            value={usd(k.gpt54_escalation_usd)}
            sub={
              ce.escalation_rate_pct != null
                ? `${ce.escalation_rate_pct}% of ${int(ce.qe_reviewed_segments)} QE segments · ${int(
                    ce.escalation_requests
                  )} requests`
                : "no QE segments in this range"
            }
            icon={Zap}
            tid="spend-kpi-escalation"
          />
        </section>

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SpendChart
            series={series}
            valueKey="cost_usd"
            label={`Spend over time (${granularity})`}
            accent="#3b82f6"
            format={(v) => usd(v, 4)}
            footnote={`total ${usd(k.total_spend_usd, 4)}`}
            testId="spend-chart-total"
          />
          <SpendChart
            series={series}
            valueKey="cost_per_minute_usd"
            label={`Cost per video minute (${granularity})`}
            accent="#10b981"
            format={(v) => usd(v, 4)}
            footnote="peak bucket shown above · hover for each bucket"
            testId="spend-chart-per-minute"
          />
        </section>

        {/* Provider breakdown */}
        <section>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Provider breakdown</h2>
          <div className="border border-zinc-800 overflow-x-auto" data-testid="spend-provider-table">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-3">Provider</th>
                  <th className="text-left px-3 py-3">Model</th>
                  <th className="text-right px-3 py-3">Requests</th>
                  <th className="text-right px-3 py-3">Input</th>
                  <th className="text-right px-3 py-3">Cached</th>
                  <th className="text-right px-3 py-3">Output</th>
                  <th className="text-right px-3 py-3">Audio min</th>
                  <th className="text-right px-3 py-3">USD</th>
                  <th className="text-right px-3 py-3">INR</th>
                  <th className="text-right px-3 py-3">Unpriced</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.provider_breakdown || []).map((r) => (
                  <tr
                    key={`${r.provider}-${r.model}`}
                    className="border-t border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`spend-provider-row-${r.model}`}
                  >
                    <td className="px-3 py-2.5 text-white">{r.provider}</td>
                    <td className="px-3 py-2.5 text-zinc-300 font-mono">{r.model}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(r.requests)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(r.input_tokens)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(r.cached_input_tokens)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(r.output_tokens)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">
                      {r.audio_minutes ? mins(r.audio_minutes) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-white font-mono">{usd(r.cost_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-300 font-mono">
                      {r.cost_inr ? inr(r.cost_inr) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-zinc-500">{int(r.unavailable)}</td>
                  </tr>
                ))}
                {(summary?.provider_breakdown || []).length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center text-zinc-500 py-6">
                      No cost events recorded in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Operation breakdown */}
        <section>
          <h2 className="font-heading text-2xl font-bold tracking-tight mb-3">Pipeline stage breakdown</h2>
          <div className="border border-zinc-800 overflow-x-auto" data-testid="spend-operation-table">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-3">Operation</th>
                  <th className="text-right px-3 py-3">Requests</th>
                  <th className="text-right px-3 py-3">Input</th>
                  <th className="text-right px-3 py-3">Output</th>
                  <th className="text-right px-3 py-3">USD</th>
                  <th className="text-right px-3 py-3">INR</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary?.by_operation || {}).map(([op, b]) => (
                  <tr key={op} className="border-t border-zinc-800/60" data-testid={`spend-operation-row-${op}`}>
                    <td className="px-3 py-2.5 text-zinc-300 font-mono">{op}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(b.requests)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(b.input_tokens)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(b.output_tokens)}</td>
                    <td className="px-3 py-2.5 text-right text-white font-mono">{usd(b.cost_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-300 font-mono">
                      {b.cost_inr ? inr(b.cost_inr) : "—"}
                    </td>
                  </tr>
                ))}
                {Object.keys(summary?.by_operation || {}).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-zinc-500 py-6">
                      No stage-level costs in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Per-video history */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Per-video cost history</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                setQ(qInput);
              }}
              className="relative"
            >
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="job id"
                data-testid="spend-job-search"
                className="h-9 pl-8 pr-3 bg-zinc-950 border border-zinc-800 text-xs font-mono w-56 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </form>
          </div>

          {counters.jobs_without_telemetry > 0 && (
            <p
              className="text-[11px] font-mono text-zinc-500 mb-2"
              data-testid="spend-telemetry-unavailable-note"
            >
              {int(counters.jobs_without_telemetry)} of {int(counters.jobs_created_in_range)} jobs in this range have
              no cost events (processed before cost telemetry was enabled) — reported as “Cost telemetry
              unavailable”, never estimated.
            </p>
          )}

          <div className="border border-zinc-800 overflow-x-auto" data-testid="spend-jobs-table">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-3">Job</th>
                  <th className="text-left px-3 py-3">Date</th>
                  <th className="text-right px-3 py-3">Duration</th>
                  <th className="text-right px-3 py-3">Whisper</th>
                  <th className="text-right px-3 py-3">DeepSeek</th>
                  <th className="text-right px-3 py-3">Gemini</th>
                  <th className="text-right px-3 py-3">GPT-5.4</th>
                  <th className="text-right px-3 py-3">Total USD</th>
                  <th className="text-right px-3 py-3">Total INR</th>
                  <th className="text-right px-3 py-3">$/min</th>
                  <th className="text-right px-3 py-3">Esc.</th>
                  <th className="text-left px-3 py-3">Telemetry</th>
                </tr>
              </thead>
              <tbody>
                {(jobs.rows || []).map((r) => (
                  <tr
                    key={r.job_id}
                    onClick={() => setDetailJob(r.job_id)}
                    data-testid={`spend-job-row-${r.job_id}`}
                    className="border-t border-zinc-800/60 hover:bg-zinc-900/60 cursor-pointer"
                  >
                    <td className="px-3 py-2.5 font-mono text-zinc-300">{r.job_id.slice(0, 16)}</td>
                    <td className="px-3 py-2.5 text-zinc-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{mins(r.duration_minutes)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400 font-mono">{usd(r.whisper_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400 font-mono">{usd(r.deepseek_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400 font-mono">{usd(r.gemini_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400 font-mono">{usd(r.gpt54_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-white font-mono">{usd(r.total_usd)}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-300 font-mono">
                      {r.total_inr ? inr(r.total_inr) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-zinc-400 font-mono">
                      {r.cost_per_minute_usd ? usd(r.cost_per_minute_usd, 4) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{int(r.gpt_escalations)}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${
                          r.usage_status === "complete"
                            ? "border-emerald-500/40 text-emerald-300"
                            : r.usage_status === "partial"
                            ? "border-amber-500/40 text-amber-300"
                            : "border-zinc-700 text-zinc-500"
                        }`}
                      >
                        {r.usage_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {(jobs.rows || []).length === 0 && (
                  <tr>
                    <td colSpan={12} className="text-center text-zinc-500 py-6" data-testid="spend-jobs-empty">
                      No video has cost telemetry in this range — nothing is estimated here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {jobs.pages > 1 && (
            <div className="flex items-center justify-between mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <span>
                Page {jobs.page} / {jobs.pages} · {int(jobs.total)} videos
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={jobs.page <= 1}
                  data-testid="spend-jobs-prev"
                  className="border border-zinc-800 px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-30"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={jobs.page >= jobs.pages}
                  data-testid="spend-jobs-next"
                  className="border border-zinc-800 px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-30"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>

        <WhisperOverlapAudit rangeQuery={qs()} />

        <UnitEconomics rangeQuery={qs()} />

        <SpendAlerts />

        <WeeklyDigest />

        <section className="text-[11px] font-mono text-zinc-600 border-t border-zinc-900 pt-4">
          {int(counters.events)} cost events · {int(counters.priced_events)} priced ·{" "}
          {int(counters.events_without_inr)} without INR · {int(counters.unattributed_events)} not attributable to a
          job ({usd(counters.unattributed_usd)})
        </section>
      </main>

      {detailJob && <SpendJobDetail jobId={detailJob} onClose={() => setDetailJob(null)} />}
    </div>
  );
}
